import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import { DataGridFilters } from './DataGridFilters';
import type { ActiveFilter, DataGridFilter } from './types';

export interface DataGridToolbarProps {
  totalCount: number;
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  filters?: DataGridFilter[];
  activeFilters?: ActiveFilter[];
  onFilterChange?: (filters: ActiveFilter[]) => void;
  exportable?: boolean;
  onExport?: () => void;
  refreshable?: boolean;
  onRefresh?: () => void;
  autoRefreshOptions?: number[];
  autoRefreshInterval?: number;
  onAutoRefreshChange?: (interval: number) => void;
  toolbarActions?: ReactNode;
}

export function DataGridToolbar({
  totalCount,
  searchable = false,
  searchValue = '',
  onSearchChange,
  filters = [],
  activeFilters = [],
  onFilterChange,
  exportable = false,
  onExport,
  refreshable = false,
  onRefresh,
  autoRefreshOptions = [5, 10, 30, 60],
  autoRefreshInterval = 0,
  onAutoRefreshChange,
  toolbarActions,
}: DataGridToolbarProps) {
  const [searchInput, setSearchInput] = useState(searchValue);
  const debouncedSearch = useDebounce(searchInput, 300);
  const hasBuiltInToolbarControls =
    searchable || (exportable && Boolean(onExport)) || (refreshable && Boolean(onRefresh)) || Boolean(onAutoRefreshChange);
  const usesCustomToolbarLayout = Boolean(toolbarActions) && !hasBuiltInToolbarControls;

  useEffect(() => {
    if (onSearchChange && debouncedSearch !== searchValue) {
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, onSearchChange, searchValue]);

  return (
    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 space-y-3">
      {usesCustomToolbarLayout ? (
        <div className="w-full">{toolbarActions}</div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-semibold text-gray-900 dark:text-gray-100">{totalCount}</span> total rows
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {searchable && (
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search..."
                className="w-56 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                aria-label="Search table rows"
              />
            )}

            {exportable && onExport && (
              <button
                type="button"
                onClick={onExport}
                className="inline-flex items-center gap-1 rounded-md bg-primary-700 hover:bg-primary-800 text-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none"
              >
                <span aria-hidden="true">📄</span>
                Export CSV
              </button>
            )}

            {refreshable && onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-500 focus:outline-none"
              >
                Refresh
              </button>
            )}

            {onAutoRefreshChange && (
              <select
                value={autoRefreshInterval}
                onChange={(event) => onAutoRefreshChange(Number(event.target.value))}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-2 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                aria-label="Auto-refresh interval"
              >
                <option value={0}>Auto-refresh: Off</option>
                {(autoRefreshOptions.length > 0 ? autoRefreshOptions : [5, 10, 30, 60]).map((interval) => (
                  <option key={interval} value={interval}>
                    Auto-refresh: {interval}s
                  </option>
                ))}
              </select>
            )}

            {toolbarActions}
          </div>
        </div>
      )}

      {filters.length > 0 && onFilterChange && (
        <DataGridFilters filters={filters} activeFilters={activeFilters} onFilterChange={onFilterChange} />
      )}
    </div>
  );
}
